import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  MessageSquare, Phone, Mail, Trash2, CheckCircle,
  Building2, Calendar, Search, Filter, LayoutList,
  Columns,
  Plus, X, Flame, Unlock, ShieldAlert
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import LeadDetailDrawer from '../../components/dashboard/LeadDetailDrawer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  new: 'bg-blue-100 text-blue-800 border-blue-200',
  contacted: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  qualified: 'bg-purple-100 text-purple-800 border-purple-200',
  meeting_scheduled: 'bg-pink-100 text-pink-800 border-pink-200',
  negotiation: 'bg-orange-100 text-orange-800 border-orange-200',
  closed_won: 'bg-green-100 text-green-800 border-green-200',
  closed_lost: 'bg-gray-100 text-gray-800 border-gray-200'
};

const priorityColors = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-sky-100 text-sky-600',
  high: 'bg-red-100 text-red-600'
};

export default function EnquiriesPage() {
  const { getAuthHeader, agency } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'board'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const [selectedLead, setSelectedLead] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Add Lead State
  const [showAddLead, setShowAddLead] = useState(false);
  const [newLead, setNewLead] = useState({
    name: '', phone: '', email: '', message: '', priority: 'medium'
  });
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    fetchEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter]);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/enquiries`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);

      const response = await axios.get(`${url}?${params.toString()}`, {
        headers: getAuthHeader()
      });
      setEnquiries(response.data.enquiries);
    } catch (error) {
      console.error('Failed to fetch enquiries:', error);
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleLeadClick = async (lead) => {
    setSelectedLead(lead);
    if (!lead.read) {
      // Mark as read silently
      try {
        await axios.put(`${API_URL}/api/enquiries/${lead.id}/read`, {}, { headers: getAuthHeader() });
        setEnquiries(prev => prev.map(e => e.id === lead.id ? { ...e, read: true } : e));
      } catch (err) { /* ignore */ }
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/api/enquiries/${deleteId}`, {
        headers: getAuthHeader()
      });
      setEnquiries(prev => prev.filter(e => e.id !== deleteId));
      toast.success('Enquiry deleted');
      if (selectedLead?.id === deleteId) setSelectedLead(null);
    } catch (error) {
      toast.error('Failed to delete enquiry');
    } finally {
      setDeleteId(null);
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      await axios.post(`${API_URL}/api/enquiries/manual`, newLead, {
        headers: getAuthHeader()
      });
      toast.success('Lead added successfully');
      setShowAddLead(false);
      setNewLead({ name: '', phone: '', email: '', message: '', priority: 'medium' });
      fetchEnquiries();
    } catch (error) {
      toast.error('Failed to add lead');
    } finally {
      setAddLoading(false);
    }
  };

  const handleUnlockLead = async (e, leadId) => {
    e.stopPropagation();
    try {
      const response = await axios.post(`${API_URL}/api/enquiries/${leadId}/unlock`, {}, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message);
      fetchEnquiries();
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, is_unlocked: true });
      }
    } catch (error) {
      toast.error('Failed to unlock lead');
    }
  };

  const filteredEnquiries = enquiries.filter(e =>
    e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.phone.includes(searchQuery) ||
    (e.property_title && e.property_title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  // --- Views ---

  const ListView = () => (
    <div className="space-y-3">
      {filteredEnquiries.map((enquiry) => (
        <Card
          key={enquiry.id}
          className={`hover:shadow-md transition-shadow cursor-pointer ${!enquiry.read ? 'border-l-4 border-l-sky-500 bg-sky-50/10' : ''}`}
          onClick={() => handleLeadClick(enquiry)}
        >
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-4">
            {/* Avatar & Name */}
            <div className="flex items-center gap-3 w-full md:w-1/4">
              <div className="w-10 h-10 rounded-full bg-navy-100 flex items-center justify-center text-navy-700 font-bold">
                {enquiry.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className={`font-medium text-navy-900 ${!enquiry.read ? 'font-bold' : ''}`}>{enquiry.name}</h3>
                <div className="text-xs text-navy-500 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {formatDate(enquiry.created_at)}
                </div>
                {enquiry.lead_score >= 80 && (
                  <div className="mt-1 flex items-center gap-1 text-[10px] font-bold text-orange-600 animate-pulse">
                    <Flame className="w-3 h-3 fill-orange-500" /> HOT LEAD
                  </div>
                )}
              </div>
            </div>

            {/* Status Badge */}
            <div className="w-full md:w-1/6">
              <Badge variant="outline" className={`${statusColors[enquiry.status] || 'bg-gray-100'} capitalize border px-2 py-0.5`}>
                {enquiry.status?.replace('_', ' ') || 'New'}
              </Badge>
            </div>

            {/* Property Info */}
            <div className="w-full md:w-1/3">
              {enquiry.property_title ? (
                <div className="flex items-center gap-2 text-sm text-navy-700">
                  <Building2 className="w-4 h-4 text-navy-400" />
                  <span className="truncate">{enquiry.property_title}</span>
                </div>
              ) : (
                <span className="text-sm text-navy-400 italic">General Enquiry</span>
              )}
            </div>

            {/* Priority & Assigned */}
            <div className="w-full md:w-1/6 space-y-1">
              <Badge variant="secondary" className={`${priorityColors[enquiry.priority] || 'bg-gray-100'} capitalize w-full justify-center`}>
                {enquiry.priority || 'Medium'}
              </Badge>
              {enquiry.assigned_agent_name && (
                <div className="text-[10px] text-navy-500 flex items-center gap-1 justify-center bg-navy-50 py-0.5 rounded">
                  <User className="w-2 h-2" /> {enquiry.assigned_agent_name}
                </div>
              )}
            </div>

            {/* Masked Info Indicator */}
            {agency?.subscription === 'free' && !enquiry.is_unlocked && (
              <div className="w-full md:w-auto">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1 text-xs border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100"
                  onClick={(e) => handleUnlockLead(e, enquiry.id)}
                >
                  <Unlock className="w-3 h-3" /> Unlock Lead
                </Button>
              </div>
            )}

            {/* Actions */}
            <div className="w-full md:w-auto flex justify-end">
              <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-500 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); setDeleteId(enquiry.id); }}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
      {filteredEnquiries.length === 0 && (
        <div className="text-center py-12 text-navy-500 bg-navy-50 rounded-lg border border-dashed border-navy-200">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>No leads found matching your filters.</p>
        </div>
      )}
    </div>
  );

  const BoardView = () => {
    const columns = [
      { id: 'new', label: 'New' },
      { id: 'contacted', label: 'Contacted' },
      { id: 'qualified', label: 'Qualified' },
      { id: 'meeting_scheduled', label: 'Meeting' },
      { id: 'closed_won', label: 'Won' },
    ];

    return (
      <div className="flex gap-4 overflow-x-auto pb-4 min-w-full">
        {columns.map(col => {
          const colLeads = filteredEnquiries.filter(e => e.status === col.id);
          return (
            <div key={col.id} className="min-w-[280px] w-[280px] bg-navy-50/50 rounded-lg p-3 border border-navy-100 flex flex-col h-full max-h-[calc(100vh-200px)]">
              <div className="flex items-center justify-between mb-3 px-1">
                <h4 className="font-semibold text-navy-900 capitalize">{col.label}</h4>
                <Badge variant="secondary" className="bg-white text-navy-500">{colLeads.length}</Badge>
              </div>
              <div className="space-y-3 overflow-y-auto flex-1 pr-1">
                {colLeads.map(lead => (
                  <Card key={lead.id} className="cursor-pointer hover:shadow-sm border-navy-100" onClick={() => handleLeadClick(lead)}>
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-sm text-navy-900">{lead.name}</span>
                        <Badge className={`text-[10px] px-1.5 py-0 h-5 ${priorityColors[lead.priority]}`}>{lead.priority}</Badge>
                        {lead.lead_score >= 80 && <Flame className="w-3 h-3 text-orange-500 fill-orange-500 ml-1" />}
                      </div>
                      {lead.property_title && (
                        <div className="text-xs text-navy-500 mb-2 truncate flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {lead.property_title}
                        </div>
                      )}
                      <div className="flex justify-between items-center text-xs text-navy-400 mt-2">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {formatDate(lead.created_at)}
                        </span>
                        {lead.assigned_agent_name ? (
                          <span className="font-medium text-sky-600 flex items-center gap-1 bg-sky-50 px-1.5 py-0.5 rounded">
                            <User className="w-3 h-3" /> {lead.assigned_agent_name.split(' ')[0]}
                          </span>
                        ) : (
                          <span className="font-medium text-navy-600">{lead.source}</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {colLeads.length === 0 && <div className="text-center py-4 text-xs text-navy-300 italic">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-6 h-[calc(100vh-64px)] flex flex-col">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 sticky top-0 bg-navy-50 z-10 py-2">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Lead Management</h1>
          <p className="text-navy-500 text-sm">Track and manage your potential customers</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowAddLead(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add Lead
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
          <Input
            placeholder="Search leads..."
            className="pl-9 bg-white"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] bg-white">
            <Filter className="w-3 h-3 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="qualified">Qualified</SelectItem>
            <SelectItem value="closed_won">Closed Won</SelectItem>
          </SelectContent>
        </Select>

        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[140px] bg-white">
            <SortAscIcon className="w-3 h-3 mr-2" />
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>

        <div className="border border-navy-200 rounded-lg p-1 bg-white flex items-center ml-auto">
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 px-2"
            onClick={() => setViewMode('list')}
          >
            <LayoutList className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'board' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 px-2"
            onClick={() => setViewMode('board')}
          >
            <Columns className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {loading ? (
          <div className="space-y-4 p-1">
            {[1, 2, 3].map(i => <div key={i} className="h-24 bg-navy-100 animate-pulse rounded-lg" />)}
          </div>
        ) : viewMode === 'list' ? <ListView /> : <BoardView />}
      </div>

      {/* Drawer */}
      <LeadDetailDrawer
        lead={selectedLead}
        open={!!selectedLead}
        onClose={() => setSelectedLead(null)}
        onUpdate={() => { fetchEnquiries(); if (selectedLead) setSelectedLead({ ...selectedLead }); }}
      />

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription>Are you sure? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Lead Dialog */}
      <Dialog open={showAddLead} onOpenChange={setShowAddLead}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLead} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                required
                value={newLead.name}
                onChange={e => setNewLead({ ...newLead, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  required
                  value={newLead.phone}
                  onChange={e => setNewLead({ ...newLead, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newLead.email}
                  onChange={e => setNewLead({ ...newLead, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={newLead.priority} onValueChange={v => setNewLead({ ...newLead, priority: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes / Message</Label>
              <Textarea
                value={newLead.message}
                onChange={e => setNewLead({ ...newLead, message: e.target.value })}
                placeholder="Initial requirements..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddLead(false)}>Cancel</Button>
              <Button type="submit" disabled={addLoading}>Add Lead</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function SortAscIcon({ className }) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M11 11h4" /><path d="M11 15h4" /><path d="M11 19h4" /><path d="M10 21V3" /><path d="M10 7a4 4 0 0 0-8 0 4 4 0 0 0 8 0" /></svg>;
}
